import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Alert, CircularProgress, Dialog, DialogActions, DialogContent, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { SampleMethodologyService } from "../../../Component/Api/VerificationAgency/SampleMethodology/SampleMethodologyService";
import { AppQuill } from "../../../Component/Common/Components/FormValidationInputs/AppQuill";
import AppTextInput from "../../../Component/Common/Components/FormValidationInputs/AppTextInput";
import { MDTypography } from "../../../Component/Common/Components/MDTypography";
import { SampleMethodology } from "../../../Component/Model/Dto/VerificationAgency/SampleMethodology";
import { VAElements } from "../../../Component/Model/Enum/BEE123Enums";
import { CustomDialogTitle } from "../../Common/Custom/CustomDialogTitle";
import { SampleMethodologyScheme } from "./SampleMethodologiesValidation";
import { SmallLoading } from "../../../Component/Common/Components/Loading/IntermediateLoading";

interface IProps {
    sampleMethodologyId: string;
    showEditMethodologyModal: boolean;
    isEditable: boolean;
    closeEditMethodologyModal: () => void;
}

export const SampleMethodologiesEditModal = (props: IProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [sampleMethodology, setSampleMethodology] = useState<SampleMethodology.SampleMethodologyData>();
    const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const { control, handleSubmit, formState: { isSubmitting }, reset } = useForm({
        mode: "all",
        resolver: yupResolver(SampleMethodologyScheme),
    });

    const handleDataSubmit = async (data: FieldValues) => {
        if (data.lowRisk > data.mediumRisk) {
            setShowErrorMessage(true);
            setErrorMessage("Low Risk percentage should be less than or equal to Medium Risk percentage.")
        }
        else if (data.mediumRisk > data.highRisk) {
            setShowErrorMessage(true);
            setErrorMessage("Medium Risk percentage should be less than or equal to High Risk percentage.")
        }
        else {
            try {
                setShowErrorMessage(false);
                const finalData = {
                    id: sampleMethodology?.id,
                    elementId: sampleMethodology?.elementId,
                    policyDetail: data.policyDetail,
                    sampleLow: data.lowRisk,
                    sampleMedium: data.mediumRisk,
                    sampleHigh: data.highRisk,
                    sampleInterview: data.interviewSamplePercentage
                } as SampleMethodology.SampleMethodologyEditRequest
                await SampleMethodologyService.updateSampleMethodology(finalData);
                toast.success("Changes saved successfully");
                props.closeEditMethodologyModal();
            } catch (error) {
                console.log(error)
            }
        }
    }

    const getSampleMethodologyDetails = async () => {
        setIsLoading(true);
        try {
            const response = await SampleMethodologyService.getSampleMethodologyById(props.sampleMethodologyId);
            const formData = {
                policyDetail: response.data.policyDetail,
                lowRisk: response.data.sampleLow,
                mediumRisk: response.data.sampleMedium,
                highRisk: response.data.sampleHigh,
                interviewSamplePercentage: response.data.sampleInterview,
            };
            setSampleMethodology(response.data)
            reset(formData);
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }
    }
    const handleSave = async () => {
        await handleSubmit(handleDataSubmit)()
            .catch(() => {
                toast.error("An error occured while saving");
            });
    }

    useEffect(() => {
        getSampleMethodologyDetails();
    }, [])

    return (
        <Dialog disableEnforceFocus open={props.showEditMethodologyModal} onClose={() => { props.closeEditMethodologyModal() }} aria-labelledby="form-dialog-title" maxWidth='lg'>
            <CustomDialogTitle id="assign-dialog-title" onClose={() => props.closeEditMethodologyModal()}>
                Edit {sampleMethodology && VAElements[sampleMethodology.elementId]} Methodology
            </CustomDialogTitle>
            <DialogContent sx={{ width: '950px' }}>
                {isLoading ?
                    <SmallLoading />
                    :
                    <>
                        <form id='IndividualForm'>
                            <Grid container>
                                <MDTypography variant="h6">Policy Detail</MDTypography>
                            </Grid>
                            <AppQuill
                                control={control}
                                name="policyDetail"
                                placeholder="Write something..."
                                maxLength={5000}
                                disabled={!props.isEditable}
                            />

                            {showErrorMessage &&
                                <Alert severity="error" sx={{ mt: 2 }}>{errorMessage}</Alert>
                            }

                            <Grid container mt={2} spacing={2}>
                                <Grid item xs={5}>
                                    <MDTypography variant="body2">
                                        Sample Percentage
                                    </MDTypography>
                                </Grid>
                                <Grid item xs={7} container spacing={2}>
                                    <Grid item xs={4}>
                                        <AppTextInput control={control} name="lowRisk" label="Low Risk" value={sampleMethodology?.sampleLow.toString()} disabled={!props.isEditable}/>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <AppTextInput control={control} name="mediumRisk" label="Medium Risk" value={sampleMethodology?.sampleMedium.toString()} disabled={!props.isEditable}/>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <AppTextInput control={control} name="highRisk" label="High Risk" value={sampleMethodology?.sampleHigh.toString()} disabled={!props.isEditable}/>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid container mt={1} spacing={2}>
                                <Grid item xs={5}>
                                    <MDTypography variant="body2">
                                        Interview Sample Percentage
                                    </MDTypography>
                                </Grid>
                                <Grid item xs={7}>
                                    <AppTextInput control={control} name="interviewSamplePercentage" label="Interview Sample Precentage" value={sampleMethodology?.sampleInterview.toString()} disabled={!props.isEditable}/>
                                </Grid>
                            </Grid>
                        </form>
                    </>
                }
            </DialogContent>
            <DialogActions>
                {props.isEditable &&
                    <LoadingButton
                        disabled={isLoading}
                        loading={isSubmitting}
                        variant='contained'
                        form='EditForm'
                        type="submit"
                        color='primary'
                        onClick={handleSave}>
                        Save
                    </LoadingButton>
                }
            </DialogActions>
        </Dialog >
    )
}